// React Query hooks for /staff/statistics/*. Mirrors useExhibits +
// useZones. Each query is keyed by its parameters so different filter
// combinations cache independently.
//
// The CSV export is a one-shot side-effect (file download) without
// cacheable state, so it stays as a plain async helper in stats.ts; no
// hook here.

import { useQuery } from "@tanstack/react-query";

import {
  DailyVisitorCount,
  HourlyParams,
  HourlyVisitorCount,
  ItemViewCount,
  ItemsParams,
  RangeParams,
  StatsSummary,
  VisitorStatsRow,
  getDailyCounts,
  getHourlyStats,
  getItemStats,
  getStatsSummary,
  getVisitorStats,
} from "../stats";

export const statsKeys = {
  all: ["stats"] as const,
  summary: () => [...statsKeys.all, "summary"] as const,
  hourly: (params: HourlyParams) =>
    [...statsKeys.all, "hourly", params] as const,
  items: (params: ItemsParams) => [...statsKeys.all, "items", params] as const,
  dailyCounts: (params: RangeParams) =>
    [...statsKeys.all, "daily-counts", params] as const,
  visitors: (params: RangeParams) =>
    [...statsKeys.all, "visitors", params] as const,
};

export function useStatsSummary() {
  return useQuery<StatsSummary>({
    queryKey: statsKeys.summary(),
    queryFn: getStatsSummary,
  });
}

export function useHourlyStats(params: HourlyParams | undefined) {
  return useQuery<HourlyVisitorCount[]>({
    queryKey: statsKeys.hourly(params ?? { date: "" }),
    queryFn: () => getHourlyStats(params as HourlyParams),
    enabled: Boolean(params?.date),
  });
}

export function useItemStats(params: ItemsParams | undefined) {
  return useQuery<ItemViewCount[]>({
    queryKey: statsKeys.items(params ?? { start_date: "", end_date: "" }),
    queryFn: () => getItemStats(params as ItemsParams),
    enabled: Boolean(params?.start_date && params?.end_date),
  });
}

export function useDailyCounts(params: RangeParams | undefined) {
  return useQuery<DailyVisitorCount[]>({
    queryKey: statsKeys.dailyCounts(params ?? { start_date: "", end_date: "" }),
    queryFn: () => getDailyCounts(params as RangeParams),
    enabled: Boolean(params?.start_date && params?.end_date),
  });
}

export function useVisitorStats(params: RangeParams | undefined) {
  return useQuery<VisitorStatsRow[]>({
    queryKey: statsKeys.visitors(params ?? { start_date: "", end_date: "" }),
    queryFn: () => getVisitorStats(params as RangeParams),
    enabled: Boolean(params?.start_date && params?.end_date),
  });
}
